import React, { useState, useEffect, useRef, useMemo } from "react";
import { safeScrollToTop } from "../utils/scrollGuard";
import { useNavigate, useLocation } from "react-router-dom";
import { useScrollLock } from "../context/ScrollLockContext";
import { APP_URLS } from "../config/urls.config";
import {
  FaSearch,
  FaHeart,
  FaMapMarkerAlt,
  FaShareAlt,
  FaDirections,
  FaArrowLeft,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./HotelRoomPageStyles.css";
import ModernPhotoGallery from "./ModernPhotoGallery";
import jwtTokenService from "../services/jwtTokenService";
import HotelDetailView from "./HotelDetail/HotelDetailView";

// Helper function to get photos for a specific room based on index
const getRoomPhotos = (allPhotos, roomIndex, photosPerRoom = 4) => {
  if (!allPhotos || allPhotos.length === 0) return [];

  const totalPhotos = allPhotos.length;
  const startIndex = (roomIndex * photosPerRoom) % totalPhotos;
  const selectedPhotos = [];

  for (let i = 0; i < photosPerRoom; i++) {
    const photoIndex = (startIndex + i) % totalPhotos;
    selectedPhotos.push(allPhotos[photoIndex]);
  }

  return selectedPhotos;
};

const DEFAULT_POPULAR_DESTINATIONS = [
  "Bikaner",
  "Jaipur",
  "Jaisalmer",
  "Udaipur",
  "Delhi",
  "Mumbai",
  "Goa",
];

const normalizeLocationText = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const doesHotelMatchLocation = (hotel, locationName) => {
  const normalizedLocation = normalizeLocationText(locationName);
  if (!normalizedLocation) return false;

  const searchableFields = [
    hotel?.city,
    hotel?.state,
    hotel?.address,
    hotel?.hotelName,
  ]
    .map((value) => normalizeLocationText(value))
    .filter(Boolean);

  return searchableFields.some(
    (field) =>
      field.includes(normalizedLocation) ||
      normalizedLocation.includes(field),
  );
};

// Hotel Rooms Page - Premium Compact Search (130px) - Matches HomePage
const HotelRoomsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { unlockScroll } = useScrollLock();
  const cardRefs = useRef({});
  const [focusedHotelId, setFocusedHotelId] = useState(null);
  const [highlightedHotelId, setHighlightedHotelId] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [roomPhotos, setRoomPhotos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [hotelMinPrices, setHotelMinPrices] = useState({});
  const [hotelAmenities, setHotelAmenities] = useState({});
  const [hotelRoomCounts, setHotelRoomCounts] = useState({});
  const [hotelPhotos, setHotelPhotos] = useState({});
  const [allRooms, setAllRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [showDirectRooms, setShowDirectRooms] = useState(false);
  const [foodTypeFilter, setFoodTypeFilter] = useState("All"); // "All", "Pure Veg", "Non-Veg"
  const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
  const [selectedStarCategories, setSelectedStarCategories] = useState([]);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState([]);
  const [selectedLocalities, setSelectedLocalities] = useState([]);
  const [showAllLocalities, setShowAllLocalities] = useState(false);
  const [reviewFilter, setReviewFilter] = useState("All Reviews");
  const [sortBy, setSortBy] = useState("Popularity");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Enhanced search form state
  const [searchFormData, setSearchFormData] = useState({
    address: "",
    guests: "",
    checkIn: "",
    checkOut: "",
  });
  const [searchErrors, setSearchErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const activeFilterCount =
    selectedPriceRanges.length +
    selectedStarCategories.length +
    selectedPropertyTypes.length +
    selectedLocalities.length +
    (foodTypeFilter !== "All" ? 1 : 0);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = jwtTokenService.isAuthenticated();
      const user = jwtTokenService.getUser();
      setIsAuthenticated(authenticated);
      setCurrentUser(user);
    };

    // Check on mount
    checkAuth();

    // Listen for logout events
    const handleLogout = () => {
      setIsAuthenticated(false);
      setCurrentUser(null);
    };

    window.addEventListener('logout', handleLogout);

    // Check auth periodically (in case token expires)
    const interval = setInterval(checkAuth, 60000); // Check every minute

    return () => {
      window.removeEventListener('logout', handleLogout);
      clearInterval(interval);
    };
  }, []);

  // Handle responsive layout on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile && showMobileFilters) {
      setShowMobileFilters(false);
    }
  }, [isMobile, showMobileFilters]);

  useEffect(() => {
    if (!showMobileFilters) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showMobileFilters]);

  // Fetch all hotel photos on mount/hotels load (Mirroring RestaurantsPage behavior)
  useEffect(() => {
    if (hotels && hotels.length > 0) {
      hotels.forEach((hotel) => {
        if (hotel?.hotelId) {
          fetchHotelPhotos(hotel.hotelId);
        }
      });
    }
  }, [hotels]);

  const fetchHotelPhotos = async (hotelId) => {
    try {
      const res = await axios.get(`${APP_URLS.API}/hotel-photos/${hotelId}`);
      if (Array.isArray(res.data)) {
        // Handle both camelCase and snake_case from backend
        const mappedPhotos = res.data
          .filter(p => p && (p.photoUrl || p.photo_url || p.url))
          .map(p => ({
            ...p,
            photoUrl: p.photoUrl || p.photo_url || p.url
          }));

        if (mappedPhotos.length > 0) {
          setHotelPhotos(prev => ({
            ...prev,
            [hotelId]: mappedPhotos
          }));
        }
      }
    } catch (err) {
      // Silently fail - photos are optional
    }
  };

  // Fetch user food preference and auto-filter
  useEffect(() => {
    const fetchUserPreferences = async () => {
      const user = jwtTokenService.getUser();
      if (user?.user_id) {
        try {
          const response = await axios.get(
            `${APP_URLS.PROFILE}/preferences/${user.user_id}`,
          );

          if (response.data?.foodPreference) {
            const preference = response.data.foodPreference;
            // Map backend preference to frontend filter
            if (preference === "Vegetarian") {
              setFoodTypeFilter("Pure Veg");
            } else if (preference === "Non-Vegetarian") {
              setFoodTypeFilter("Non-Veg");
            }
            // If preference is "Both" or anything else, leave as "All"
          }
        } catch (error) {
          // Silently fail - user can still filter manually
        }
      }
    };

    fetchUserPreferences();
  }, [currentUser]); // Re-fetch when currentUser changes

  // Filter hotels based on both search forms (moved here to avoid TDZ error)
  const filteredHotels = useMemo(() => {
    let filtered = hotels.filter((hotel) => {
      const term = searchTerm.toLowerCase();
      const formTerm = searchFormData.address.toLowerCase();

      const matchesSearchTermLocal = (text, trm) => {
        if (!text) return false;
        if (text.toLowerCase() === trm) return true;
        const regex = new RegExp(`(^|[^a-z0-9])${trm}([^a-z0-9]|$)`, 'i');
        return regex.test(text.toLowerCase());
      };

      const matchesSearchBar =
        !term ||
        (hotel.city && hotel.city.toLowerCase() === term) ||
        (hotel.hotelName && hotel.hotelName.toLowerCase().includes(term)) ||
        (hotel.city && hotel.city.toLowerCase().includes(term)) ||
        (hotel.state && hotel.state.toLowerCase().includes(term)) ||
        (hotel.country && hotel.country.toLowerCase().includes(term)) ||
        (hotel.address && matchesSearchTermLocal(hotel.address, term));

      const matchesSearchForm =
        !formTerm ||
        (hotel.city && hotel.city.toLowerCase() === formTerm) ||
        (hotel.hotelName && hotel.hotelName.toLowerCase().includes(formTerm)) ||
        (hotel.city && hotel.city.toLowerCase().includes(formTerm)) ||
        (hotel.state && hotel.state.toLowerCase().includes(formTerm)) ||
        (hotel.address && matchesSearchTermLocal(hotel.address, formTerm));

      // Food Type filter - Check foodType field from database
      const foodType = hotel?.foodType || hotel?.food_type;
      const matchesFoodType =
        foodTypeFilter === "All" || (foodType && foodType === foodTypeFilter);

      // Price Range Filter
      const minPrice = hotelMinPrices[hotel.hotelId] || 2500; // Default price if not found
      let matchesPrice = true;
      if (selectedPriceRanges.length > 0) {
        matchesPrice = selectedPriceRanges.some(range => {
          if (range === "₹0 - ₹2000") return minPrice <= 2000;
          if (range === "₹2000 - ₹4000") return minPrice > 2000 && minPrice <= 4000;
          if (range === "₹4000 - ₹8000") return minPrice > 4000 && minPrice <= 8000;
          if (range === "₹8000+") return minPrice > 8000;
          return false;
        });
      }

      // Star Category Filter (Simulated based on hotel name length or random fallback as backend might not have it)
      // Star category simulated deterministically from hotelId (3, 4, or 5 stars)
      const starCategory = 3 + (hotel.hotelId % 3); // Simulates 3, 4, 5 stars deterministically
      let matchesStar = true;
      if (selectedStarCategories.length > 0) {
        matchesStar = selectedStarCategories.includes(starCategory);
      }

      // Property Type Filter (Simulating based on name keywords or fallback)
      let pType = "Hotel";
      if (hotel.hotelName.includes("Resort")) pType = "Resort";
      else if (hotel.hotelName.includes("Villa")) pType = "Villa";
      else if (hotel.hotelName.includes("Apartment") || hotel.hotelName.includes("Inn")) pType = "Apartment";
      else if (hotel.hotelName.includes("Homestay")) pType = "Homestay";

      let matchesType = true;
      if (selectedPropertyTypes.length > 0) {
        matchesType = selectedPropertyTypes.includes(pType);
      }

      // Locality Filter - derived dynamically from hotel addresses
      let matchesLocality = true;
      if (selectedLocalities.length > 0) {
        const hotelAddr = (hotel.address || "").toLowerCase();
        matchesLocality = selectedLocalities.some(loc => hotelAddr.includes(loc.toLowerCase()));
      }

      return matchesSearchBar && matchesSearchForm && matchesFoodType && matchesPrice && matchesStar && matchesType && matchesLocality;
    });


    // Sorting Logic
    if (sortBy === "priceLowToHigh") {
      filtered.sort((a, b) => (hotelMinPrices[a.hotelId] || 0) - (hotelMinPrices[b.hotelId] || 0));
    } else if (sortBy === "priceHighToLow") {
      filtered.sort((a, b) => (hotelMinPrices[b.hotelId] || 0) - (hotelMinPrices[a.hotelId] || 0));
    } else {
      // Default "Popularity": most rooms first (hotels with more room options rank higher)
      filtered.sort((a, b) => (hotelRoomCounts[b.hotelId] || 0) - (hotelRoomCounts[a.hotelId] || 0));
    }

    return filtered;
  }, [hotels, searchTerm, searchFormData.address, foodTypeFilter, selectedPriceRanges, selectedStarCategories, selectedPropertyTypes, selectedLocalities, sortBy, hotelMinPrices, hotelRoomCounts]);

  // Derive unique localities from currently visible (pre-locality-filtered) hotels' addresses
  const dynamicLocalities = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const formTerm = searchFormData.address.toLowerCase();
    const localitySet = new Set();
    hotels.forEach(hotel => {
      const matchesTerm =
        !term ||
        (hotel.hotelName && hotel.hotelName.toLowerCase().includes(term)) ||
        (hotel.address && hotel.address.toLowerCase().includes(term)) ||
        (hotel.city && hotel.city.toLowerCase().includes(term)) ||
        (hotel.state && hotel.state.toLowerCase().includes(term));
      const matchesForm =
        !formTerm ||
        (hotel.hotelName && hotel.hotelName.toLowerCase().includes(formTerm)) ||
        (hotel.address && hotel.address.toLowerCase().includes(formTerm)) ||
        (hotel.city && hotel.city.toLowerCase().includes(formTerm)) ||
        (hotel.state && hotel.state.toLowerCase().includes(formTerm));
      if (matchesTerm && matchesForm && hotel.address) {
        // Extract meaningful locality words from address (capitalize first letter)
        const parts = hotel.address.split(/[,]+/).map(p => p.trim()).filter(p => p.length > 2 && p.length < 40);
        parts.forEach(p => localitySet.add(p));
      }
    });
    return Array.from(localitySet).slice(0, 20);
  }, [hotels, searchTerm, searchFormData.address]);

  const visibleLocalities = showAllLocalities ? dynamicLocalities : dynamicLocalities.slice(0, 4);
  const popularHotelDestinations = useMemo(() => {
    const cities = hotels
      .map((hotel) => (hotel?.city || "").trim())
      .filter(Boolean);

    return Array.from(new Set([...cities, ...DEFAULT_POPULAR_DESTINATIONS])).slice(0, 7);
  }, [hotels]);

  const activeHotelSearchLocation = useMemo(() => {
    const explicitLocation = (searchFormData.address || searchTerm || "").trim();
    return explicitLocation;
  }, [searchFormData.address, searchTerm]);

  const hotelMapQuery = useMemo(() => {
    const queryBase = (searchTerm || searchFormData.address || activeHotelSearchLocation || "").trim();
    return `${queryBase || "Hotels"} hotels`;
  }, [searchTerm, searchFormData.address, activeHotelSearchLocation]);

  // Handler for clearing all filters
  const handleClearFilters = () => {
    setFoodTypeFilter("All");
    setSelectedPriceRanges([]);
    setSelectedStarCategories([]);
    setSelectedPropertyTypes([]);
    setSelectedLocalities([]);
    setSortBy("Popularity");
  };

  // Helper for toggling array filters
  const toggleFilter = (item, state, setState) => {
    if (state.includes(item)) {
      setState(state.filter(i => i !== item));
    } else {
      setState([...state, item]);
    }
  };

  // SIMPLE SCROLL RESTORATION: Check sessionStorage on mount
  useEffect(() => {
    fetchHotels();

    // Pre-populate search from BookingHomePage navigation
    if (location.state?.searchData) {
      const { destination, checkIn, checkOut, guests } = location.state.searchData;
      if (destination) {
        setSearchTerm(destination);
        setSearchFormData(prev => ({
          ...prev,
          address: destination,
          checkIn: checkIn || prev.checkIn,
          checkOut: checkOut || prev.checkOut,
          guests: guests?.toString() || prev.guests,
        }));
      }
    }

    // Check location.state for selectedHotelId (from Travel page)
    if (location.state?.selectedHotelId) {
      const selectedId = location.state.selectedHotelId.toString();
      setFocusedHotelId(selectedId);
      // Also scroll to the card after data loads
      setTimeout(() => {
        const card = cardRefs.current[selectedId];
        if (card) {
          card.scrollIntoView({ behavior: "smooth", block: "center" });
          setHighlightedHotelId(selectedId);
          setTimeout(() => setHighlightedHotelId(null), 3000);
        }
      }, 1000);
      return;
    }

    // Check URL parameters for shared hotel link
    const params = new URLSearchParams(location.search);
    const hotelIdFromUrl = params.get("id");

    if (hotelIdFromUrl) {
      setFocusedHotelId(hotelIdFromUrl);
      // Also scroll to the card after data loads
      setTimeout(() => {
        const card = cardRefs.current[hotelIdFromUrl];
        if (card) {
          card.scrollIntoView({ behavior: "smooth", block: "center" });
          setHighlightedHotelId(hotelIdFromUrl);
          setTimeout(() => setHighlightedHotelId(null), 3000);
        }
      }, 1000);
    } else {
      const focusId = sessionStorage.getItem("focusHotelId");
      if (focusId) {
        sessionStorage.removeItem("focusHotelId");
        setFocusedHotelId(focusId);
      }
    }
  }, [location.search, location.state]);

  // When focusedHotelId is set, scroll to that card
  useEffect(() => {
    if (!focusedHotelId) {
      return;
    }

    if (!hotels || hotels.length === 0) {
      return;
    }

    let attempts = 0;

    const attemptScroll = () => {
      const card = cardRefs.current[focusedHotelId];

      if (card) {
        card.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedHotelId(focusedHotelId);
        setTimeout(() => setHighlightedHotelId(null), 3000);
        unlockScroll();
      } else if (attempts < 50) {
        attempts++;
        setTimeout(attemptScroll, 100);
      } else {
        unlockScroll();
      }
    };

    attemptScroll();
  }, [focusedHotelId, hotels, filteredHotels, unlockScroll]);

  // Fetch all rooms from all hotels
  const fetchAllRooms = async () => {
    try {
      const hotelsResponse = await axios.get(
        `${APP_URLS.HOTELS}`,
      );
      const allHotels = hotelsResponse.data;

      const allRoomsData = [];
      for (const hotel of allHotels) {
        try {
          const roomsResponse = await axios.get(
            `${APP_URLS.HOTELS}/${hotel.hotelId}/rooms`,
          );
          const hotelRooms = roomsResponse.data.map((room) => ({
            ...room,
            hotel: hotel,
          }));
          allRoomsData.push(...hotelRooms);
        } catch (error) {
          console.error(
            `Error fetching rooms for hotel ${hotel.hotelId}:`,
            error,
          );
        }
      }
      setAllRooms(allRoomsData);
    } catch (error) {
      console.error("Error fetching all rooms:", error);
    }
  };

  const fetchHotels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${APP_URLS.HOTELS}`);
      setHotels(response.data);

      // Fetch all rooms for filtering
      await fetchAllRooms();

      // Fetch minimum room prices and photos for each hotel
      const pricePromises = response.data.map(async (hotel) => {
        try {
          const roomsResponse = await axios.get(
            `${APP_URLS.HOTELS}/${hotel.hotelId}/rooms`,
          );
          if (roomsResponse.data && roomsResponse.data.length > 0) {
            const prices = roomsResponse.data.map(
              (room) => room.pricePerNight || 0,
            );
            const minPrice = Math.min(...prices);
            const amenitySet = new Set();
            roomsResponse.data.forEach((room) => {
              if (room.amenities) {
                room.amenities.split(",").map(a => a.trim()).filter(Boolean).forEach(a => amenitySet.add(a));
              }
            });
            return { hotelId: hotel.hotelId, minPrice, amenities: [...amenitySet], roomCount: roomsResponse.data.length };
          }
          return { hotelId: hotel.hotelId, minPrice: null, amenities: [], roomCount: 0 };
        } catch (error) {
          console.error(
            `Error fetching rooms for hotel ${hotel.hotelId}:`,
            error,
          );
          return { hotelId: hotel.hotelId, minPrice: null, amenities: [], roomCount: 0 };
        }
      });

      // Fetch hotel photos for all hotels
      const photoPromises = response.data.map(async (hotel) => {
        try {
          const photosResponse = await axios.get(
            `${APP_URLS.API}/hotel-photos/${hotel.hotelId}`,
          );
          const mappedPhotos = (photosResponse.data || [])
            .filter(p => p && (p.photoUrl || p.photo_url || p.url))
            .map(p => ({
              ...p,
              photoUrl: p.photoUrl || p.photo_url || p.url
            }));
          return {
            hotelId: hotel.hotelId,
            photos: mappedPhotos,
          };
        } catch (error) {
          console.error(
            `Error fetching photos for hotel ${hotel.hotelId}:`,
            error,
          );
          return { hotelId: hotel.hotelId, photos: [] };
        }
      });

      const priceResults = await Promise.all(pricePromises);
      const photoResults = await Promise.all(photoPromises);

      const pricesMap = {};
      const amenitiesMap = {};
      const roomCountsMap = {};
      priceResults.forEach((result) => {
        pricesMap[result.hotelId] = result.minPrice;
        amenitiesMap[result.hotelId] = result.amenities || [];
        roomCountsMap[result.hotelId] = result.roomCount || 0;
      });
      setHotelMinPrices(pricesMap);
      setHotelAmenities(amenitiesMap);
      setHotelRoomCounts(roomCountsMap);

      const photosMap = {};
      photoResults.forEach((result) => {
        photosMap[result.hotelId] = result.photos;
      });
      setHotelPhotos(photosMap);
    } catch (error) {
      console.error("Error fetching hotels:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomsByHotel = async (hotelId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${APP_URLS.HOTELS}/${hotelId}/rooms`,
      );
      setRooms(response.data);
    } catch (error) {
      console.error("Error fetching rooms:", error);
      // Fallback: try to get all available rooms and filter
      try {
        const allRoomsResponse = await axios.get(
          `${APP_URLS.API}/available-rooms`,
        );
        const filteredRooms = allRoomsResponse.data.filter(
          (room) => room.hotel && room.hotel.hotelId === hotelId,
        );
        setRooms(filteredRooms);
      } catch (fallbackError) {
        console.error("Error fetching rooms (fallback):", fallbackError);
        setRooms([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleHotelClick = async (hotel) => {
    setSelectedHotel(hotel);
    fetchRoomsByHotel(hotel.hotelId);

    // Scroll to top of page when hotel is selected (guarded)
    safeScrollToTop();
    // Fetch room photos for slider (from hotel_photos table)
    try {
      const res = await axios.get(
        `${APP_URLS.API}/hotel-photos/${hotel.hotelId}`,
      );
      if (Array.isArray(res.data)) {
        const mappedPhotos = res.data
          .filter(p => p && (p.photoUrl || p.photo_url || p.url))
          .map(p => ({
            ...p,
            photoUrl: p.photoUrl || p.photo_url || p.url
          }));

        setRoomPhotos(mappedPhotos);
        // Also update the global hotelPhotos object for the detail view
        setHotelPhotos(prev => ({
          ...prev,
          [hotel.hotelId]: mappedPhotos
        }));
      } else {
        setRoomPhotos([]);
      }
    } catch {
      setRoomPhotos([]);
    }
  };

  const handleBackToHotels = () => {
    setSelectedHotel(null);
    setRooms([]);
  };

  const handleRoomBooking = (room) => {
    // Check if user is authenticated
    if (!jwtTokenService.isAuthenticated()) {
      toast.error("Please login to book a room");
      navigate("/login");
      return;
    }

    // Navigate to room booking page
    navigate(`/room-booking/${room.roomId}`, {
      state: { room, hotel: selectedHotel },
    });
  };

  // Filter rooms based on search criteria (similar to restaurant page)
  useEffect(() => {
    // Check if user has filled any search criteria
    const hasSearchCriteria =
      searchFormData.address ||
      searchFormData.guests ||
      searchFormData.checkIn ||
      searchFormData.checkOut;

    if (!hasSearchCriteria || allRooms.length === 0) {
      setShowDirectRooms(false);
      setFilteredRooms([]);
      return;
    }

    const filtered = allRooms.filter((room) => {
      if (!room || !room.hotel) return false;

      const hotel = room.hotel;
      const address = hotel.address || "";
      const city = hotel.city || "";
      const hotelName = hotel.hotelName || "";
      const maxPersons = room.maxPersons || 0;

      // Location filter
      const matchesLocation =
        !searchFormData.address ||
        address.toLowerCase().includes(searchFormData.address.toLowerCase()) ||
        city.toLowerCase().includes(searchFormData.address.toLowerCase()) ||
        hotelName.toLowerCase().includes(searchFormData.address.toLowerCase());

      // Guests filter
      const requestedGuests = searchFormData.guests
        ? parseInt(searchFormData.guests)
        : 0;
      const matchesGuests = !requestedGuests || maxPersons >= requestedGuests;

      // Availability filter (room should be available)
      const matchesAvailability = room.availability !== false;

      return matchesLocation && matchesGuests && matchesAvailability;
    });

    setFilteredRooms(filtered);
    setShowDirectRooms(filtered.length > 0);
  }, [
    allRooms,
    searchFormData.address,
    searchFormData.guests,
    searchFormData.checkIn,
    searchFormData.checkOut,
  ]);

  // Enhanced search form handlers
  const handleSearchFormChange = (e) => {
    const { name, value } = e.target;
    setSearchFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (searchErrors[name]) {
      setSearchErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateSearchForm = () => {
    const errors = {};
    const today = new Date().toISOString().split("T")[0];

    // Address validation - check both searchFormData.address and searchTerm since location input updates searchTerm
    if (!searchFormData.address.trim() && !searchTerm.trim()) {
      errors.address = "Please enter destination or address";
    }

    // Guests validation
    if (!searchFormData.guests || parseInt(searchFormData.guests) < 1) {
      errors.guests = "Number of guests must be at least 1";
    } else if (parseInt(searchFormData.guests) > 10) {
      errors.guests = "Maximum 10 guests allowed";
    }

    // Check-in date validation
    if (!searchFormData.checkIn) {
      errors.checkIn = "Please select check-in date";
    } else if (searchFormData.checkIn < today) {
      errors.checkIn = "Check-in date cannot be in the past";
    }

    // Check-out date validation
    if (!searchFormData.checkOut) {
      errors.checkOut = "Please select check-out date";
    } else if (searchFormData.checkOut <= searchFormData.checkIn) {
      errors.checkOut = "Check-out date must be after check-in date";
    }

    setSearchErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRoomSearch = (e) => {
    e.preventDefault();

    if (validateSearchForm()) {
      const searchData = {
        address: (searchFormData.address || searchTerm).trim(),
        guests: parseInt(searchFormData.guests),
        checkIn: searchFormData.checkIn,
        checkOut: searchFormData.checkOut,
      };

      // Sync searchFormData.address with searchTerm if needed
      if (!searchFormData.address.trim() && searchTerm.trim()) {
        setSearchFormData(prev => ({ ...prev, address: searchTerm.trim() }));
      }

      // The filtering is already handled by filteredHotels above (moved to useMemo)
    }
  };

  // Share hotel using Web Share API (like RestaurantsPage)
  const handleHotelShare = (e, hotel) => {
    e.stopPropagation();
    const hotelUrl = `${window.location.origin}/HotelRoomsPage?id=${hotel.hotelId}`;
    const shareText = `Check out this amazing hotel: ${hotel.hotelName}!\n\n📍 Location: ${hotel.address || hotel.city}\n\nBook now for the best rates: ${hotelUrl}`;

    if (navigator.share) {
      navigator.share({
        title: hotel.hotelName,
        text: shareText,
        url: hotelUrl
      }).catch((err) => {
        navigator.clipboard.writeText(shareText).then(() => toast.success("Details copied to clipboard!"));
      });
    } else {
      navigator.clipboard.writeText(shareText).then(() => {
        toast.success("Link copied!");
      }).catch(() => {
        toast.error("Failed to copy link");
      });
    }
  };

  // Get user's current location
  const handleNearMe = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setGettingLocation(true);
    toast.info("Getting your location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });

        try {
          // Use reverse geocoding to get location name
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&accept-language=en&lat=${latitude}&lon=${longitude}`,
          );
          const data = await response.json();

          const locationName =
            data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.state ||
            "Your Location";
          const hasLocationMatch = hotels.some((hotel) =>
            doesHotelMatchLocation(hotel, locationName),
          );

          if (hasLocationMatch) {
            setSearchTerm(locationName);
            setSearchFormData((prev) => ({
              ...prev,
              address: locationName,
            }));
            toast.success(`Location found: ${locationName}`);
          } else {
            setSearchTerm("");
            setSearchFormData((prev) => ({
              ...prev,
              address: "",
            }));
            toast.info(
              `Detected ${locationName}. Showing all available hotels because no exact city match was found.`,
            );
          }
        } catch (error) {
          console.error("Error with reverse geocoding:", error);
          toast.warning("Location found, but couldn't get address details");
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        console.error("Geolocation error:", error);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error(
              "Location access denied. Please enable location permissions in your browser.",
            );
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out.");
            break;
          default:
            toast.error("An error occurred while getting your location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#2E2E2E",
        background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        minHeight: "100vh",
        paddingTop: "90px",
      }}
    >
      <>
        {/* Hero Section with Image Slider - Hidden when viewing specific hotel */}
        {!selectedHotel && (
          <section
            style={{
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Beautiful Image Slider */}
            <div
              style={{
                position: "relative",
                borderRadius: "15px",
                margin: "10px",
                boxShadow: "0 15px 35px rgba(0,0,0,0.3)",
                overflow: "hidden",
              }}
            >
              <Slider
                dots={false}
                infinite={true}
                speed={1200}
                slidesToShow={1}
                slidesToScroll={1}
                autoplay={true}
                autoplaySpeed={5000}
                arrows={false}
                fade={true}
                cssEase="cubic-bezier(0.645, 0.045, 0.355, 1.000)"
                dotsClass="slick-dots custom-hero-dots"
              >
                {[
                  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&auto=format&fit=crop&q=80",
                  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=1920&auto=format&fit=crop&q=80",
                  "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1920&auto=format&fit=crop&q=80",
                  "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1920&auto=format&fit=crop&q=80",
                  "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1920&auto=format&fit=crop&q=80",
                ].map((image, index) => (
                  <div key={index}>
                    <div
                      style={{
                        position: "relative",
                        height: isMobile ? "300px" : "500px",
                        background: `url(${image}) center/cover no-repeat`,
                        borderRadius: "15px",
                      }}
                    >
                      {/* Dark Overlay for Better Text Readability */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </Slider>

              {/* Overlay Content - Title */}
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                  zIndex: 2,
                  width: "90%",
                  maxWidth: "800px",
                }}
              >
                <h1
                  style={{
                    fontSize: isMobile ? "2rem" : "2.5rem",
                    fontWeight: "700",
                    color: "#FFFFFF",
                    letterSpacing: "-0.5px",
                    textShadow: "0 2px 12px rgba(0,0,0,0.4)",
                    lineHeight: "1.2",
                  }}
                >
                  Find Your Perfect Stay
                </h1>
                <p
                  style={{
                    fontSize: isMobile ? "0.95rem" : "1.1rem",
                    fontWeight: "400",
                    color: "rgba(255,255,255,0.95)",
                    marginTop: "8px",
                    textShadow: "0 1px 8px rgba(0,0,0,0.3)",
                  }}
                >
                  World's leading chain of hotels and homes
                </p>
              </div>

              {/* Advanced Search Bar - Desktop Only Inside Slider - Hidden when viewing specific hotel */}
              {!isMobile && !selectedHotel && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "40px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 3,
                    width: "90%",
                    maxWidth: "1200px",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "1200px",
                      margin: "0 auto",
                    }}
                  >
                    <form
                      onSubmit={handleRoomSearch}
                      style={{
                        background: "#FFFFFF",
                        borderRadius: "12px",
                        padding: "0",
                        boxShadow:
                          "0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 20px rgba(0, 0, 0, 0.15)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2.2fr 1.5fr 1.5fr 1.3fr 130px",
                          gap: "0",
                          alignItems: "stretch",
                        }}
                      >
                        {/* Hotel Location */}
                        <div
                          style={{
                            borderRight: "1px solid #E5E7EB",
                            padding: "18px 24px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Search city, area, or hotel"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              style={{
                                flex: 1,
                                padding: "0",
                                fontSize: "1.05rem",
                                border: "none",
                                background: "transparent",
                                color: "#1F2937",
                                outline: "none",
                                fontWeight: "500",
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleNearMe}
                              disabled={gettingLocation}
                              style={{
                                padding: "6px 10px",
                                background: gettingLocation
                                  ? "#FEE2E2"
                                  : "transparent",
                                border: "1px solid #E5E7EB",
                                borderRadius: "6px",
                                fontSize: "0.75rem",
                                color: gettingLocation ? "#991B1B" : "#EF4444",
                                cursor: gettingLocation ? "wait" : "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                transition: "all 0.2s ease",
                                opacity: gettingLocation ? 0.8 : 1,
                              }}
                              onMouseOver={(e) => {
                                if (!gettingLocation) {
                                  e.target.style.background = "#FEF2F2";
                                  e.target.style.borderColor = "#EF4444";
                                }
                              }}
                              onMouseOut={(e) => {
                                if (!gettingLocation) {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "#E5E7EB";
                                }
                              }}
                            >
                              <FaMapMarkerAlt
                                size={10}
                                style={{
                                  animation: gettingLocation
                                    ? "pulse 1.5s infinite"
                                    : "none",
                                }}
                              />
                              {gettingLocation ? "Finding..." : "Near me"}
                            </button>
                          </div>
                        </div>

                        {/* Check-in Date */}
                        <div
                          style={{
                            borderRight: "1px solid #E5E7EB",
                            padding: "18px 24px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#6B7280",
                              marginBottom: "4px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Check-in
                          </div>
                          <input
                            type="text"
                            placeholder="Fri, 17 Oct"
                            onFocus={(e) => {
                              e.target.type = "date";
                              e.target.min = new Date()
                                .toISOString()
                                .split("T")[0];
                            }}
                            onBlur={(e) => {
                              if (!e.target.value) e.target.type = "text";
                            }}
                            name="checkIn"
                            value={searchFormData.checkIn}
                            onChange={handleSearchFormChange}
                            style={{
                              width: "100%",
                              padding: "0",
                              fontSize: "1.05rem",
                              border: "none",
                              background: "transparent",
                              color: "#1F2937",
                              outline: "none",
                              fontWeight: "500",
                              cursor: "pointer",
                            }}
                          />
                        </div>

                        {/* Check-out Date */}
                        <div
                          style={{
                            borderRight: "1px solid #E5E7EB",
                            padding: "18px 24px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#6B7280",
                              marginBottom: "4px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Check-out
                          </div>
                          <input
                            type="text"
                            placeholder="Sat, 18 Oct"
                            onFocus={(e) => {
                              e.target.type = "date";
                              e.target.min =
                                searchFormData.checkIn ||
                                new Date().toISOString().split("T")[0];
                            }}
                            onBlur={(e) => {
                              if (!e.target.value) e.target.type = "text";
                            }}
                            name="checkOut"
                            value={searchFormData.checkOut}
                            onChange={handleSearchFormChange}
                            style={{
                              width: "100%",
                              padding: "0",
                              fontSize: "1.05rem",
                              border: "none",
                              background: "transparent",
                              color: "#1F2937",
                              outline: "none",
                              fontWeight: "500",
                              cursor: "pointer",
                            }}
                          />
                        </div>

                        {/* Rooms & Guests */}
                        <div
                          style={{
                            borderRight: "1px solid #E5E7EB",
                            padding: "18px 24px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#6B7280",
                              marginBottom: "4px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Guests
                          </div>
                          <input
                            type="number"
                            placeholder="Number of guests"
                            name="guests"
                            value={searchFormData.guests}
                            onChange={handleSearchFormChange}
                            min="1"
                            style={{
                              width: "100%",
                              padding: "0",
                              fontSize: "1.05rem",
                              border: "none",
                              background: "transparent",
                              color: "#1F2937",
                              outline: "none",
                              fontWeight: "500",
                              cursor: "pointer",
                            }}
                          />
                        </div>

                        {/* Search Button */}
                        <button
                          type="submit"
                          style={{
                            padding: "0",
                            background:
                              "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                            color: "#FFFFFF",
                            border: "none",
                            fontSize: "0.95rem",
                            fontWeight: "700",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            fontFamily: "'Inter', sans-serif",
                            boxShadow: "0 4px 12px rgba(238, 46, 36, 0.25)",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background =
                              "linear-gradient(135deg, #D02820 0%, #E5B000 100%)";
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow =
                              "0 6px 16px rgba(238, 46, 36, 0.35)";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background =
                              "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow =
                              "0 4px 12px rgba(238, 46, 36, 0.25)";
                          }}
                        >
                          Search
                        </button>
                      </div>

                      {/* Popular Destinations Section */}
                      <div
                        style={{
                          padding: "14px 24px",
                          background:
                            "linear-gradient(135deg, #2E2E2E 0%, #1A1A1A 100%)",
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.9rem",
                            color: "#FFFFFF",
                            fontWeight: "600",
                            fontFamily: "'Inter', sans-serif",
                            letterSpacing: "0.3px",
                          }}
                        >
                          Popular destinations
                        </span>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap",
                          }}
                        >
                          {popularHotelDestinations.map(
                            (destination) => (
                              <button
                                key={destination}
                                type="button"
                                onClick={() => {
                                  setSearchTerm(destination);
                                  setSearchFormData((prev) => ({
                                    ...prev,
                                    address: destination,
                                  }));
                                  toast.info(
                                    `Searching hotels in ${destination}...`,
                                  );
                                }}
                                style={{
                                  padding: "6px 16px",
                                  background: "#FFFFFF",
                                  color: "#2E2E2E",
                                  border: "1px solid #E5E7EB",
                                  borderRadius: "6px",
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  fontFamily: "'Inter', sans-serif",
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.background = "#F5F5F5";
                                  e.target.style.borderColor = "#EE2E24";
                                  e.target.style.color = "#EE2E24";
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = "#FFFFFF";
                                  e.target.style.borderColor = "#E5E7EB";
                                  e.target.style.color = "#2E2E2E";
                                }}
                              >
                                {destination}
                              </button>
                            ),
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Mobile Search Section - Refined & Elegant */}
        {isMobile && !selectedHotel && (
          <section
            style={{
              padding: "8px",
              background: "transparent",
              marginTop: "-30px",
              position: "relative",
              zIndex: 10,
            }}
          >
            <div
              style={{
                background: "#FFFFFF",
                borderRadius: "20px",
                padding: "16px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                border: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              <form onSubmit={handleRoomSearch}>
                <h3 
                  style={{ 
                    fontSize: "1.1rem", 
                    fontWeight: "800", 
                    marginBottom: "14px", 
                    color: "#111827", 
                    textAlign: "center",
                    letterSpacing: "-0.02em"
                  }}
                >
                  Find Your Perfect Stay
                </h3>

                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {/* Search Input */}
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      background: "#F9FAFB",
                      borderRadius: "14px",
                      border: "1px solid #F3F4F6",
                      padding: "10px 14px",
                    }}
                  >
                    <FaSearch style={{ color: "#EE2E24", fontSize: "14px", marginRight: "10px", opacity: 0.8 }} />
                    <input
                      type="text"
                      placeholder="Where are you going?"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        flex: 1,
                        border: "none",
                        background: "transparent",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        color: "#374151",
                        outline: "none",
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleNearMe}
                      disabled={gettingLocation}
                      style={{
                        padding: "6px 12px",
                        background: gettingLocation ? "#FEF2F2" : "#FFFFFF",
                        border: "1px solid #FEE2E2",
                        borderRadius: "10px",
                        fontSize: "0.7rem",
                        color: "#EE2E24",
                        fontWeight: "700",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        boxShadow: "0 2px 4px rgba(238, 46, 36, 0.05)",
                      }}
                    >
                      <FaMapMarkerAlt size={10} />
                      {gettingLocation ? "..." : "Near me"}
                    </button>
                  </div>

                  {/* Dates Row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div
                      style={{
                        background: "#F9FAFB",
                        borderRadius: "14px",
                        border: "1px solid #F3F4F6",
                        padding: "10px 14px",
                      }}
                    >
                      <span style={{ display: "block", fontSize: "0.65rem", fontWeight: "800", color: "#9CA3AF", textTransform: "uppercase", marginBottom: "2px" }}>
                        Check-in
                      </span>
                      <input
                        type="text"
                        placeholder="Select date"
                        onFocus={e => { e.target.type = "date"; e.target.min = new Date().toISOString().split("T")[0]; }}
                        onBlur={e => { if (!e.target.value) e.target.type = "text"; }}
                        name="checkIn"
                        value={searchFormData.checkIn}
                        onChange={handleSearchFormChange}
                        style={{ width: "100%", border: "none", background: "transparent", fontSize: "0.85rem", fontWeight: "700", color: "#111827", outline: "none" }}
                      />
                    </div>
                    <div
                      style={{
                        background: "#F9FAFB",
                        borderRadius: "14px",
                        border: "1px solid #F3F4F6",
                        padding: "10px 14px",
                      }}
                    >
                      <span style={{ display: "block", fontSize: "0.65rem", fontWeight: "800", color: "#9CA3AF", textTransform: "uppercase", marginBottom: "2px" }}>
                        Check-out
                      </span>
                      <input
                        type="text"
                        placeholder="Select date"
                        onFocus={e => { e.target.type = "date"; e.target.min = searchFormData.checkIn || new Date().toISOString().split("T")[0]; }}
                        onBlur={e => { if (!e.target.value) e.target.type = "text"; }}
                        name="checkOut"
                        value={searchFormData.checkOut}
                        onChange={handleSearchFormChange}
                        style={{ width: "100%", border: "none", background: "transparent", fontSize: "0.85rem", fontWeight: "700", color: "#111827", outline: "none" }}
                      />
                    </div>
                  </div>

                  {/* Guests Box */}
                  <div
                    style={{
                      background: "#F9FAFB",
                      borderRadius: "14px",
                      border: "1px solid #F3F4F6",
                      padding: "10px 14px",
                    }}
                  >
                    <span style={{ display: "block", fontSize: "0.65rem", fontWeight: "800", color: "#9CA3AF", textTransform: "uppercase", marginBottom: "2px" }}>
                      Number of Guests
                    </span>
                    <input
                      type="number"
                      placeholder="Number of guests"
                      name="guests"
                      value={searchFormData.guests}
                      onChange={handleSearchFormChange}
                      min="1"
                      style={{ width: "100%", border: "none", background: "transparent", fontSize: "0.85rem", fontWeight: "700", color: "#111827", outline: "none" }}
                    />
                  </div>

                  {/* Search Button */}
                  <button
                    type="submit"
                    style={{
                      marginTop: "4px",
                      background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "14px",
                      padding: "14px",
                      fontSize: "0.95rem",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      boxShadow: "0 8px 20px rgba(238, 46, 36, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <FaSearch size={14} />
                    SEARCH PROPERTIES
                  </button>

                  {/* Popular Destinations Pills */}
                  <div style={{ marginTop: "12px" }}>
                    <p style={{ fontSize: "0.7rem", fontWeight: "800", color: "#9CA3AF", textAlign: "center", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.02em" }}>
                      Explore Destinations
                    </p>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
                      {popularHotelDestinations.map((destination) => (
                        <button
                          key={destination}
                          type="button"
                          onClick={() => {
                            setSearchTerm(destination);
                            setSearchFormData(prev => ({ ...prev, address: destination }));
                          }}
                          style={{
                            padding: "6px 14px",
                            background: "#F3F4F6",
                            color: "#4B5563",
                            border: "none",
                            borderRadius: "20px",
                            fontSize: "0.75rem",
                            fontWeight: "700",
                            transition: "all 0.2s ease"
                          }}
                        >
                          {destination}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </section>
        )}

        {/* Hotels List Section - MMT Inspired Layout */}
        {!selectedHotel && (
          <section
            style={{
              padding: isMobile ? "10px 0" : "20px 0",
              background: "#f2f2f2",
              minHeight: "100vh",
            }}
          >
            <div style={{ maxWidth: "1280px", margin: "0 auto", padding: isMobile ? "0 6px" : "0 10px", display: "flex", gap: "20px", flexDirection: isMobile ? "column" : "row" }}>

              {/* Sidebar Filters */}
              {!isMobile && (
                <div style={{ width: "260px", flexShrink: 0 }}>
                  {/* Map Widget */}

                  <div
                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hotelMapQuery)}`, '_blank')}
                    style={{ background: "#fff", padding: "10px", borderRadius: "8px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", marginBottom: "20px", position: "relative", cursor: "pointer", overflow: "hidden" }}
                  >
                    <div
                      style={{
                        height: "110px",
                        backgroundImage: "url('https://media.wired.com/photos/59269cd37034dc5f91bec0f1/master/pass/GoogleMapTA.jpg')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        borderRadius: "4px",

                      }}
                    />
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "100%", padding: "0 20px" }}>
                      <button style={{ width: "100%", padding: "10px", background: "#fff", color: "#0095f6", fontWeight: "700", border: "none", borderRadius: "6px", fontSize: "0.9rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", boxShadow: "0 4px 10px rgba(0,0,0,0.15)" }}>
                        EXPLORE ON MAP <FaMapMarkerAlt size={14} />
                      </button>
                    </div>
                  </div>



                  <div style={{ background: "#fff", padding: "20px", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 10px 30px rgba(0,0,0,0.04)", position: "sticky", top: "20px", maxHeight: "calc(100vh - 40px)", overflowY: "auto", scrollbarWidth: "none" }}>
                    <style>
                      {`
                        div::-webkit-scrollbar {
                          display: none;
                        }
                      `}
                    </style>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "1.1rem", fontWeight: "800", color: "#1a1a1a", margin: 0 }}>Filters</h3>
                      <button onClick={handleClearFilters} style={{ background: "transparent", border: "none", fontSize: "0.8rem", color: "#EE2E24", fontWeight: "700", cursor: "pointer", textTransform: "uppercase" }}>Clear All</button>
                    </div>

                    {/* Price Range */}
                    <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                      <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Price Range</h4>
                      {["₹0 - ₹2000", "₹2000 - ₹4000", "₹4000 - ₹8000", "₹8000+"].map((range, idx) => (
                        <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                          <input
                            type="checkbox"
                            checked={selectedPriceRanges.includes(range)}
                            onChange={() => toggleFilter(range, selectedPriceRanges, setSelectedPriceRanges)}
                            style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }}
                          />
                          <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{range}</span>
                          <span style={{ marginLeft: "auto", fontSize: "0.8rem", color: "#aaa" }}>({Math.floor(Math.random() * 50) + 1})</span>
                        </label>
                      ))}
                    </div>

                    {/* Food Preference */}
                    <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                      <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Food Preference</h4>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {["All", "Pure Veg", "Non-Veg"].map((type) => (
                          <label key={type} style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: "4px 0" }}>
                            <input
                              type="radio"
                              name="foodType"
                              checked={foodTypeFilter === type}
                              onChange={() => setFoodTypeFilter(type)}
                              style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }}
                            />
                            <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Star Category */}
                    <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                      <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Star Category</h4>
                      {[5, 4, 3].map((star) => (
                        <label key={star} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                          <input
                            type="checkbox"
                            checked={selectedStarCategories.includes(star)}
                            onChange={() => toggleFilter(star, selectedStarCategories, setSelectedStarCategories)}
                            style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }}
                          />
                          <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{star} Star</span>
                        </label>
                      ))}
                    </div>

                    {/* Property Type */}
                    <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                      <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Property Type</h4>
                      {["Hotel", "Apartment", "Resort", "Villa", "Homestay"].map((type, idx) => (
                        <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                          <input
                            type="checkbox"
                            checked={selectedPropertyTypes.includes(type)}
                            onChange={() => toggleFilter(type, selectedPropertyTypes, setSelectedPropertyTypes)}
                            style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }}
                          />
                          <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{type}</span>
                        </label>
                      ))}
                    </div>

                    {/* Top Locations - Dynamic */}
                    {dynamicLocalities.length > 0 && (
                    <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                      <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Localities</h4>
                      {visibleLocalities.map((loc, idx) => (
                        <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                          <input
                            type="checkbox"
                            checked={selectedLocalities.includes(loc)}
                            onChange={() => toggleFilter(loc, selectedLocalities, setSelectedLocalities)}
                            style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }}
                          />
                          <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{loc}</span>
                        </label>
                      ))}
                      {dynamicLocalities.length > 4 && (
                        <span
                          onClick={() => setShowAllLocalities(prev => !prev)}
                          style={{ fontSize: "0.8rem", color: "#0095f6", fontWeight: "600", cursor: "pointer" }}
                        >
                          {showAllLocalities ? "- Show Less" : `+ ${dynamicLocalities.length - 4} More`}
                        </span>
                      )}
                    </div>
                    )}

                    {/* Room Views */}
                    <div>
                      <h4 style={{ fontSize: "0.95rem", fontWeight: "700", marginBottom: "12px", color: "#333" }}>Room Views</h4>
                      {["City View", "Garden View", "Pool View"].map((view, idx) => (
                        <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                          <input type="checkbox" style={{ accentColor: "#EE2E24", width: "16px", height: "16px" }} />
                          <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#555", fontWeight: "500" }}>{view}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content */}
              <div style={{ flex: 1 }}>

              {/* Mobile Filter Trigger - Refined & Elegant */}
              {isMobile && (
                <div style={{ marginBottom: "14px", display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => setShowMobileFilters(true)}
                    style={{
                      flex: 1,
                      padding: "12px 16px",
                      borderRadius: "14px",
                      border: "1px solid #F3F4F6",
                      background: "#FFFFFF",
                      color: "#111827",
                      fontWeight: "900",
                      fontSize: "0.8rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      cursor: "pointer"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", color: "#EE2E24" }}>
                         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                      </span>
                      <span>Filters</span>
                    </div>
                    {activeFilterCount > 0 && (
                      <span style={{ background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)", color: "#FFFFFF", minWidth: "22px", height: "22px", borderRadius: "22px", fontSize: "0.7rem", fontWeight: "900", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px", boxShadow: "0 4px 10px rgba(238, 46, 36, 0.2)" }}>
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>
              )}

                {/* Mobile Filter Drawer - Refined & Elegant */}
                {isMobile && showMobileFilters && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(15, 23, 42, 0.4)",
                      backdropFilter: "blur(4px)",
                      zIndex: 1200,
                      display: "flex",
                      justifyContent: "flex-end"
                    }}
                    onClick={() => setShowMobileFilters(false)}
                  >
                    <div
                      style={{
                        width: "85%",
                        maxWidth: "340px",
                        height: "100%",
                        background: "#FFFFFF",
                        boxShadow: "-10px 0 50px rgba(0,0,0,0.15)",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "24px 0 0 24px"
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ padding: "20px", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <h3 style={{ fontSize: "1.1rem", fontWeight: "900", color: "#111827", margin: 0 }}>Filters</h3>
                          <p style={{ fontSize: "0.65rem", fontWeight: "800", color: "#9CA3AF", textTransform: "uppercase", marginTop: "2px", letterSpacing: "0.05em" }}>Customize your search</p>
                        </div>
                        <button
                          onClick={() => setShowMobileFilters(false)}
                          style={{ border: "none", background: "#F3F4F6", color: "#6B7280", width: "32px", height: "32px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", cursor: "pointer" }}
                        >
                          ✕
                        </button>
                      </div>

                      <div style={{ flex: 1, overflowY: "auto", padding: "20px", background: "#FBFBFC" }}>
                        <div style={{ background: "#FFFFFF", padding: "16px", borderRadius: "16px", border: "1px solid #F3F4F6", marginBottom: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                            <h4 style={{ fontSize: "0.85rem", fontWeight: "900", margin: 0, color: "#111827", textTransform: "uppercase", letterSpacing: "0.02em" }}>Price Range</h4>
                            <button onClick={handleClearFilters} style={{ background: "transparent", border: "none", fontSize: "0.65rem", color: "#EE2E24", fontWeight: "900", cursor: "pointer", textTransform: "uppercase" }}>Clear</button>
                          </div>
                          {["₹0 - ₹2000", "₹2000 - ₹4000", "₹4000 - ₹8000", "₹8000+"].map((range, idx) => (
                            <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                              <input
                                type="checkbox"
                                checked={selectedPriceRanges.includes(range)}
                                onChange={() => toggleFilter(range, selectedPriceRanges, setSelectedPriceRanges)}
                                style={{ accentColor: "#EE2E24", width: "18px", height: "18px" }}
                              />
                              <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#374151", fontWeight: "700" }}>{range}</span>
                            </label>
                          ))}
                        </div>

                        <div style={{ background: "#FFFFFF", padding: "16px", borderRadius: "16px", border: "1px solid #F3F4F6", marginBottom: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                          <h4 style={{ fontSize: "0.85rem", fontWeight: "900", margin: "0 0 12px 0", color: "#111827", textTransform: "uppercase", letterSpacing: "0.02em" }}>Food Preference</h4>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {["All", "Pure Veg", "Non-Veg"].map((type) => (
                              <label key={type} style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: "4px 0" }}>
                                <input
                                  type="radio"
                                  name="foodTypeMobile"
                                  checked={foodTypeFilter === type}
                                  onChange={() => setFoodTypeFilter(type)}
                                  style={{ accentColor: "#EE2E24", width: "18px", height: "18px" }}
                                />
                                <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#374151", fontWeight: "700" }}>{type}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div style={{ background: "#FFFFFF", padding: "16px", borderRadius: "16px", border: "1px solid #F3F4F6", marginBottom: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                          <h4 style={{ fontSize: "0.85rem", fontWeight: "900", margin: "0 0 12px 0", color: "#111827", textTransform: "uppercase", letterSpacing: "0.02em" }}>Star Category</h4>
                          {[5, 4, 3].map((star) => (
                            <label key={star} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                              <input
                                type="checkbox"
                                checked={selectedStarCategories.includes(star)}
                                onChange={() => toggleFilter(star, selectedStarCategories, setSelectedStarCategories)}
                                style={{ accentColor: "#EE2E24", width: "18px", height: "18px" }}
                              />
                              <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#374151", fontWeight: "700" }}>{star} Star</span>
                            </label>
                          ))}
                        </div>

                        <div style={{ background: "#FFFFFF", padding: "16px", borderRadius: "16px", border: "1px solid #F3F4F6", marginBottom: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                          <h4 style={{ fontSize: "0.85rem", fontWeight: "900", margin: "0 0 12px 0", color: "#111827", textTransform: "uppercase", letterSpacing: "0.02em" }}>Property Type</h4>
                          {["Hotel", "Apartment", "Resort", "Villa", "Homestay"].map((type, idx) => (
                            <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                              <input
                                type="checkbox"
                                checked={selectedPropertyTypes.includes(type)}
                                onChange={() => toggleFilter(type, selectedPropertyTypes, setSelectedPropertyTypes)}
                                style={{ accentColor: "#EE2E24", width: "18px", height: "18px" }}
                              />
                              <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#374151", fontWeight: "700" }}>{type}</span>
                            </label>
                          ))}
                        </div>

                        {dynamicLocalities.length > 0 && (
                          <div style={{ background: "#FFFFFF", padding: "16px", borderRadius: "16px", border: "1px solid #F3F4F6", marginBottom: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                            <h4 style={{ fontSize: "0.85rem", fontWeight: "900", margin: "0 0 12px 0", color: "#111827", textTransform: "uppercase", letterSpacing: "0.02em" }}>Localities</h4>
                            {visibleLocalities.map((loc, idx) => (
                              <label key={idx} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer", padding: "4px 0" }}>
                                <input
                                  type="checkbox"
                                  checked={selectedLocalities.includes(loc)}
                                  onChange={() => toggleFilter(loc, selectedLocalities, setSelectedLocalities)}
                                  style={{ accentColor: "#EE2E24", width: "18px", height: "18px" }}
                                />
                                <span style={{ marginLeft: "10px", fontSize: "0.9rem", color: "#374151", fontWeight: "700" }}>{loc}</span>
                              </label>
                            ))}
                            {dynamicLocalities.length > 4 && (
                              <span
                                onClick={() => setShowAllLocalities(prev => !prev)}
                                style={{ fontSize: "0.75rem", color: "#EE2E24", fontWeight: "900", cursor: "pointer", textTransform: "uppercase", marginTop: "8px", display: "inline-block" }}
                              >
                                {showAllLocalities ? "- Show Less" : `+ ${dynamicLocalities.length - 4} More`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div style={{ padding: "16px 20px", borderTop: "1px solid #F3F4F6", background: "#FFFFFF", display: "flex", gap: "10px" }}>
                        <button
                          onClick={handleClearFilters}
                          style={{
                            flex: 1,
                            padding: "14px",
                            borderRadius: "14px",
                            border: "1px solid #F3F4F6",
                            background: "#FFFFFF",
                            color: "#6B7280",
                            fontWeight: "900",
                            fontSize: "0.75rem",
                            cursor: "pointer",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em"
                          }}
                        >
                          Reset
                        </button>
                        <button
                          onClick={() => setShowMobileFilters(false)}
                          style={{
                            flex: 1.5,
                            padding: "14px",
                            borderRadius: "14px",
                            border: "none",
                            background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                            color: "#FFFFFF",
                            fontWeight: "900",
                            fontSize: "0.75rem",
                            cursor: "pointer",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            boxShadow: "0 8px 20px rgba(238, 46, 36, 0.2)"
                          }}
                        >
                          Show Results
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Header & Sort - Restored */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? "12px" : "20px", flexWrap: "wrap", gap: "8px" }}>
                  <h2 style={{ fontSize: isMobile ? "1rem" : "1.5rem", fontWeight: "700", color: "#1a1a1a", margin: 0 }}>
                    {showDirectRooms
                      ? `${filteredRooms.length} Rooms Found`
                      : activeHotelSearchLocation
                        ? `${filteredHotels.length} Properties in ${activeHotelSearchLocation}`
                        : `${filteredHotels.length} Properties Found`}
                  </h2>
                </div>

                <div style={{ background: "#fff", padding: isMobile ? "8px 10px" : "12px 20px", borderRadius: "12px", marginBottom: isMobile ? "12px" : "20px", display: "flex", alignItems: "center", boxShadow: "0 4px 15px rgba(0,0,0,0.03)", gap: isMobile ? "6px" : "25px", border: "1px solid #f5f5f5", overflowX: "auto" }}>
                  <span style={{ fontSize: "0.85rem", fontWeight: "800", color: "#777", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap", display: isMobile ? "none" : "inline" }}>Sort By:</span>
                  <div style={{ display: "flex", gap: isMobile ? "4px" : "10px", overflowX: "auto", scrollbarWidth: "none", flex: 1 }}>
                    {[
                      { label: "Popularity", value: "popularity" },
                      { label: "Price (Low to High)", value: "priceLowToHigh" },
                      { label: "Price (High to Low)", value: "priceHighToLow" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setSortBy(option.value)}
                        style={{
                          padding: isMobile ? "5px 10px" : "6px 16px",
                          borderRadius: "20px",
                          border: "none",
                          background: sortBy === option.value ? "linear-gradient(90deg, #EE2E24, #FFC400)" : "transparent",
                          color: sortBy === option.value ? "#fff" : "#555",
                          fontSize: isMobile ? "0.72rem" : "0.85rem",
                          fontWeight: "700",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Loading State */}
                {loading && (
                  <div style={{ textAlign: "center", padding: "40px" }}>
                    <div style={{ display: "inline-block", width: "40px", height: "40px", border: "3px solid #eee", borderTop: "3px solid #EE2E24", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                    <p style={{ marginTop: "10px", color: "#666" }}>Finding best stays for you...</p>
                  </div>
                )}

                {/* Content */}
                {showDirectRooms ? (
                  /* Room Cards Grid (Preserved) */
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "20px" }}>
                    {filteredRooms.map((room, roomIndex) => {
                      /* Reusing Existing Room Card Logic but simplified for placement */
                      const roomPhotosData = getRoomPhotos(hotelPhotos[room.hotel?.hotelId] || [], roomIndex, 4);
                      return (
                        /* ... Room Card Implementation ... */
                        <div key={room.roomId} style={{
                          background: "#fff",
                          borderRadius: "16px",
                          overflow: "hidden",
                          border: "1px solid #f0f0f0",
                          boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                          transition: "transform 0.3s ease"
                        }}
                          onMouseEnter={e => e.currentTarget.style.transform = "translateY(-4px)"}
                          onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                        >
                          <div style={{ height: "180px", background: "#f8f8f8", position: "relative" }}>
                            <img src={roomPhotosData[0]?.photoUrl} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" />
                            <span style={{
                              position: "absolute",
                              top: "12px",
                              left: "12px",
                              background: "rgba(255,255,255,0.9)",
                              padding: "4px 10px",
                              borderRadius: "8px",
                              fontSize: "0.65rem",
                              fontWeight: "900",
                              color: "#EE2E24"
                            }}>{room.roomType.toUpperCase()}</span>
                          </div>
                          <div style={{ padding: "20px" }}>
                            <h3 style={{ fontSize: "1rem", fontWeight: "900", marginBottom: "4px", color: "#1a1a1a" }}>Room {room.roomNumber}</h3>
                            <p style={{ fontSize: "0.8rem", color: "#666", marginBottom: "15px", fontWeight: "600" }}>{room.hotel?.hotelName}</p>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                <span style={{ fontSize: "1.2rem", fontWeight: "900", color: "#1a1a1a" }}>₹{room.pricePerNight}</span>
                                <span style={{ fontSize: "0.65rem", color: "#999", display: "block" }}>per night</span>
                              </div>
                              <button
                                onClick={() => handleRoomBooking(room)}
                                style={{
                                  background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                                  color: "#fff",
                                  border: "none",
                                  padding: "8px 16px",
                                  borderRadius: "10px",
                                  fontWeight: "900",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  boxShadow: "0 4px 10px rgba(238, 46, 36, 0.2)"
                                }}
                              >BOOK NOW</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Hotel List - MMT Style */
                  <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    {filteredHotels.map((hotel) => (
                      <div
                        key={hotel.hotelId}
                        className="hotel-card"
                        style={{
                          display: "flex",
                          flexDirection: isMobile ? "column" : "row",
                          background: "#fff",
                          borderRadius: "16px",
                          border: "1px solid #f0f0f0",
                          overflow: "hidden",
                          transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                          cursor: "pointer",
                          minHeight: isMobile ? "auto" : "220px",
                          marginBottom: "16px",
                          position: "relative"
                        }}
                        onClick={() => handleHotelClick(hotel)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.06)";
                          e.currentTarget.style.borderColor = "#eee";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "none";
                          e.currentTarget.style.borderColor = "#f0f0f0";
                        }}
                      >
                        {/* Left: Image Section */}
                        <div className="hotel-card-image-wrap" style={{
                          width: isMobile ? "100%" : "280px",
                          minHeight: isMobile ? "185px" : "100%",
                          position: "relative",
                          background: "#f0f0f0",
                          overflow: "hidden"
                        }}>
                          {hotelPhotos[hotel.hotelId] && hotelPhotos[hotel.hotelId][0] ? (
                            <img
                              src={hotelPhotos[hotel.hotelId][0].photoUrl}
                              alt={hotel.hotelName}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                              }}
                              onError={(e) => {
                                e.target.src = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&auto=format&fit=crop&q=80";
                                e.target.onerror = null; // Prevent infinite loop
                              }}
                              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                            />
                          ) : (
                            <img
                              src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&auto=format&fit=crop&q=80"
                              alt="Fallback"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          )}
                          <div style={{
                            position: "absolute",
                            top: "12px",
                            left: "12px",
                            background: "rgba(255, 255, 255, 0.9)",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            fontSize: "0.65rem",
                            fontWeight: "900",
                            color: "#EE2E24",
                            backdropFilter: "blur(4px)"
                          }}>
                            VERIFIED
                          </div>
                          {(hotelPhotos[hotel.hotelId]?.length > 1) && (
                            <div style={{ position: "absolute", bottom: "12px", left: "12px", background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", color: "#fff", padding: "4px 8px", borderRadius: "10px", fontSize: "0.65rem", fontWeight: "700" }}>
                              {hotelPhotos[hotel.hotelId].length} PHOTOS
                            </div>
                          )}
                        </div>

                        {/* Middle: Info */}
                        <div className="hotel-card-details" style={{ flex: 1, padding: isMobile ? "12px 14px" : "20px", borderRight: isMobile ? "none" : "1px solid #f0f0f0", display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                              <h3 style={{ fontSize: isMobile ? "0.95rem" : "1.2rem", fontWeight: "900", marginBottom: "4px", color: "#1a1a1a", letterSpacing: "-0.5px" }}>{hotel.hotelName}</h3>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: isMobile ? "8px" : "12px", color: "#666", fontSize: "0.8rem", fontWeight: "500" }}>
                                <FaMapMarkerAlt style={{ color: "#EE2E24" }} size={12} />
                                <span>{hotel.address || hotel.city}</span>
                              </div>
                            </div>
                            {/* Badges */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "5px", alignItems: "flex-end" }}>
                              {/* Badges Removed as requested */}
                            </div>
                          </div>

                          {/* View on Map */}
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((hotel.hotelName || "") + " " + (hotel.address || hotel.city || ""))}`, '_blank');
                              }}
                              style={{
                                display: "flex", alignItems: "center", gap: "6px",
                                padding: "5px 12px", borderRadius: "20px",
                                border: "1.5px solid #EE2E24", background: "#fff",
                                color: "#EE2E24", fontSize: "0.75rem", fontWeight: "800",
                                cursor: "pointer", whiteSpace: "nowrap"
                              }}
                            >
                              <FaMapMarkerAlt size={11} /> View on Map
                            </button>
                          </div>

                          {/* Amenities Preview */}
                          <div style={{ marginTop: "auto", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {(() => {
                              const amenities = hotelAmenities[hotel.hotelId] || [];
                              const chips = amenities.length > 0 ? amenities.slice(0, 4) : (hotel.foodType ? [hotel.foodType] : []);
                              return chips.map((amenity, i) => (
                                <span key={i} style={{ fontSize: "0.7rem", color: "#555", fontWeight: "700", background: "#f0f0f0", padding: "4px 10px", borderRadius: "20px", border: "1px solid #e5e5e5", textTransform: "capitalize" }}>
                                  {amenity}
                                </span>
                              ));
                            })()}
                          </div>
                        </div>

                        {/* Right: Price & Action */}
                        <div className="hotel-card-booking" style={{ width: isMobile ? "100%" : "220px", padding: isMobile ? "10px 14px" : "20px", display: "flex", flexDirection: isMobile ? "row" : "column", justifyContent: isMobile ? "space-between" : "center", alignItems: isMobile ? "center" : "flex-end", background: "#fafafa", borderLeft: isMobile ? "none" : "1px solid #f5f5f5", borderTop: isMobile ? "1px solid #f0f0f0" : "none" }}>
                          <div style={{ textAlign: isMobile ? "left" : "right", marginBottom: isMobile ? "0" : "15px" }}>
                            {hotelMinPrices[hotel.hotelId] ? (
                              <>
                                <span style={{ display: "block", fontSize: "0.75rem", color: "#999", textDecoration: "line-through", fontWeight: "600" }}>₹{Math.round(hotelMinPrices[hotel.hotelId] * 1.3)}</span>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: isMobile ? "flex-start" : "flex-end" }}>
                                  <h3 style={{ fontSize: isMobile ? "1.3rem" : "1.7rem", fontWeight: "900", color: "#1a1a1a", margin: 0, letterSpacing: "-1px" }}>₹{hotelMinPrices[hotel.hotelId]}</h3>
                                </div>
                                <span style={{ fontSize: "0.7rem", color: "#777", fontWeight: "600" }}>+ GST & Taxes</span>
                                {!isMobile && <div style={{ fontSize: "0.65rem", color: "#27ae60", fontWeight: "800", marginTop: "4px", textTransform: "uppercase" }}>Limited Time Offer</div>}
                              </>
                            ) : (
                              <div style={{ color: "#EE2E24", fontWeight: "900", fontSize: isMobile ? "0.8rem" : "0.9rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Check Rates
                              </div>
                            )}
                          </div>

                          <div style={{ width: isMobile ? "auto" : "100%" }}>
                            <button style={{
                              width: "100%",
                              padding: isMobile ? "8px 16px" : "12px",
                              background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                              color: "#fff",
                              border: "none",
                              borderRadius: "12px",
                              fontWeight: "900",
                              cursor: "pointer",
                              fontSize: isMobile ? "0.75rem" : "0.85rem",
                              textTransform: "uppercase",
                              marginBottom: isMobile ? "0" : "12px",
                              boxShadow: "0 6px 15px rgba(238, 46, 36, 0.2)",
                              transition: "all 0.3s ease",
                              letterSpacing: "0.5px",
                              whiteSpace: "nowrap"
                            }}
                              onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
                              onMouseLeave={e => e.currentTarget.style.filter = "none"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHotelClick(hotel);
                              }}
                            >
                              View Deals
                            </button>

                            {/* Share & Directions Actions */}
                            <div style={{ display: isMobile ? "none" : "flex", gap: "10px", justifyContent: "flex-end", marginTop: "10px" }}>
                              <button
                                onClick={(e) => handleHotelShare(e, hotel)}
                                style={{ background: "transparent", border: "1px solid #ddd", borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#555" }}
                                title="Share"
                              >
                                <FaShareAlt size={14} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const address = `${hotel.hotelName}, ${hotel.address || hotel.city}`;
                                  window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
                                }}
                                style={{ background: "transparent", border: "1px solid #ddd", borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#555" }}
                                title="Directions"
                              >
                                <FaDirections size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )
        }

        {/* MMT Style Hotel Detail Page - Now Modular */}
        {
          selectedHotel && (
            <HotelDetailView
              selectedHotel={selectedHotel}
              rooms={rooms}
              hotelPhotos={hotelPhotos}
              isAuthenticated={isAuthenticated}
              navigate={navigate}
              handleBackToHotels={handleBackToHotels}
              handleRoomBooking={handleRoomBooking}
            />
          )
        }
      </>
    </div >
  );
};

export default HotelRoomsPage;
